import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { getProbabilityScoresList } from "../../services/api-server/probabilities";
import {
	getProbabilityScore,
	getSeverityDescription,
} from "../../utils/riskMatrix";
import RiskMatrixModal from "./RiskMatrixModal";

// A consequence or a potential risk, as the report forms store it.
type RiskEntry = {
	consequence?: unknown;
	severity_level?: unknown;
	probability?: unknown;
};

// A stored name, letter or number as text. Only text and numbers are shown:
// nothing checks what else could be stored.
const asLabel = (value: unknown): string | undefined => {
	if (typeof value === "number" && Number.isFinite(value)) return String(value);
	return typeof value === "string" && value !== "" ? value : undefined;
};

// A stored list's entries; any of them can be missing.
const asEntries = (value: unknown): (RiskEntry | null | undefined)[] =>
	Array.isArray(value) ? value : [];

// What a severity letter means for a consequence, if the risk matrix lists it.
const getSeverityMeaning = (consequence?: string, level?: string) => {
	if (!consequence || !level) return undefined;
	const meaning = getSeverityDescription(consequence, level);
	return meaning === "-" ? undefined : meaning;
};

// A severity letter or a probability number, with what it means.
function LevelValue({
	level,
	meaning,
}: Readonly<{ level?: string; meaning?: string }>) {
	if (!level) return <span>—</span>;
	return (
		<>
			<span className="report-risks__level">{level}</span>
			{meaning && <span>{meaning}</span>}
		</>
	);
}

type ReportConsequencesProps = {
	primary?: RiskEntry | null;
	additional?: unknown;
};

// An incident's consequences, each with its severity: the primary one, then
// the others, numbered from 2 as the report form numbers them.
export function ReportConsequences({
	primary,
	additional,
}: Readonly<ReportConsequencesProps>) {
	const headingId = useId();
	const cards = [
		{ key: "primary", label: "Primary consequence", entry: primary },
		...asEntries(additional).map((entry, index) => ({
			key: `additional-${index}`,
			label: `Consequence ${index + 2}`,
			entry,
		})),
	];

	return (
		<section className="report-risks" aria-labelledby={headingId}>
			<h3 id={headingId} className="report-risks__title">
				Consequences
			</h3>
			<div className="report-risks__grid">
				{cards.map(({ key, label, entry }) => {
					const consequence = asLabel(entry?.consequence);
					const level = asLabel(entry?.severity_level);
					return (
						<article key={key} className="report-risks__card">
							<p className="report-risks__number">{label}</p>
							<p className="report-risks__name">{consequence ?? "—"}</p>
							<dl className="report-risks__facts">
								<div>
									<dt>Severity</dt>
									<dd>
										<LevelValue
											level={level}
											meaning={getSeverityMeaning(consequence, level)}
										/>
									</dd>
								</div>
							</dl>
						</article>
					);
				})}
			</div>
		</section>
	);
}

type ReportPotentialRisksProps = {
	risks?: unknown;
	// The report's tenant, whose probability list says what each probability
	// number means.
	operator?: string;
};

// A report's potential risks, each with its score, severity and probability,
// and the risk matrix that scores them.
export function ReportPotentialRisks({
	risks,
	operator,
}: Readonly<ReportPotentialRisksProps>) {
	const headingId = useId();
	const entries = asEntries(risks);

	const { data: probabilities } = useQuery({
		queryKey: ["probabilities", operator],
		queryFn: async () => {
			if (!operator) return [];
			try {
				const result = await getProbabilityScoresList(
					operator,
					{},
					{ index: 1 }
				);
				if (Array.isArray(result)) {
					return result;
				}
				return [];
			} catch (error) {
				console.error("Error fetching probabilities:", error);
				return [];
			}
		},
		// Nothing to name without potential risks.
		enabled: !!operator && entries.length > 0,
	});

	const getProbabilityMeaning = (probability: unknown) =>
		asLabel(
			probabilities?.find((item: any) => item?.index === probability)
				?.probability
		);

	// By position, as the report form labels them.
	const cards = entries.map((entry, index) => ({
		key: `risk-${index}`,
		label: `Potential risk ${index + 1}`,
		entry,
	}));

	return (
		<section className="report-risks" aria-labelledby={headingId}>
			<div className="report-risks__head">
				<h3 id={headingId} className="report-risks__title">
					Potential risks{" "}
					{entries.length > 0 && (
						<span className="report-risks__count">{entries.length}</span>
					)}
				</h3>
				{entries.length > 0 && <RiskMatrixModal buttonLabel="Risk matrix" />}
			</div>
			{entries.length === 0 ? (
				<p className="report-risks__empty">No potential risks recorded</p>
			) : (
				<div className="report-risks__grid">
					{cards.map(({ key, label, entry }) => {
						const consequence = asLabel(entry?.consequence);
						const level = asLabel(entry?.severity_level);
						const probability = asLabel(entry?.probability);
						// Such as "D3 - HIGH"; none where the matrix has no score.
						const score = getProbabilityScore(level ?? "", probability ?? "");
						const scoreLevel = score?.split(" - ")[1]?.toLowerCase() ?? "none";
						return (
							<article key={key} className="report-risks__card">
								<div className="report-risks__card-head">
									<p className="report-risks__number">{label}</p>
									<span
										className={`report-risks__score report-risks__score--${scoreLevel}`}
									>
										{score ?? "N/A"}
									</span>
								</div>
								<p className="report-risks__name">{consequence ?? "—"}</p>
								<dl className="report-risks__facts">
									<div>
										<dt>Severity</dt>
										<dd>
											<LevelValue
												level={level}
												meaning={getSeverityMeaning(consequence, level)}
											/>
										</dd>
									</div>
									<div>
										<dt>Probability</dt>
										<dd>
											<LevelValue
												level={probability}
												meaning={getProbabilityMeaning(entry?.probability)}
											/>
										</dd>
									</div>
								</dl>
							</article>
						);
					})}
				</div>
			)}
		</section>
	);
}
